import {
  SurveyBuilderOptIn,
  SurveyBuilderRootDocument,
  defaultTermsAndPrivacyOptIn,
  defaultSmsOptIn,
  VirtualOptIns,
  styleLabelText,
} from "@max/common";
import { AnalysisDocument } from "@max/common/src/artists/analysis";
import {
  CollectionReference,
  DocumentReference,
  collection,
  doc,
  getDocs,
  getFirestore,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { SurveyTemplateConfig } from "../dashboard/Components/Templates/types";
import { removeUndefined } from "Utils";

interface OptInsResponse {
  optIns: SurveyBuilderOptIn[];
  disclaimers?: string[];
  virtualOptIns?: VirtualOptIns;
}

interface Options {
  defaultImage?: string;
}

export const createSurveyBuilder = async (
  params: SurveyTemplateConfig,
  options: Options,
) => {
  const survey = doc(
    collection(getFirestore(), "set_fan_builder_surveys"),
  ) as DocumentReference<SurveyBuilderRootDocument>;

  const analysisSnap = await getDocs(
    query(
      collection(
        getFirestore(),
        "artist_groups",
        params.artistGroupId,
        "analysis",
      ) as CollectionReference<AnalysisDocument>,
      where("images", "!=", null),
      orderBy("createdAt", "desc"),
    ),
  );

  const analysisImage = analysisSnap.docs
    ?.map((doc) => doc.data()?.images)
    ?.flat()
    ?.find((data) => data.url);

  if (analysisImage?.url) {
    const theme = analysisImage.templates?.[0]?.themes?.find(
      ({ name }) => name === "Swiper",
    );

    params.fields = {
      ...params.fields,
      image: {
        content: { en: analysisImage.url },
      },
      ...(theme?.primary && {
        backgroundColor: {
          content: theme.primary.hex,
        },
        buttonColor: {
          content: theme.primary.hex,
        },
      }),
    };
  } else {
    params.fields = {
      ...params.fields,
      ...(options.defaultImage && {
        image: { content: { en: options.defaultImage } },
      }),
      backgroundColor: { content: "#000000" },
      buttonColor: { content: "#000000" },
    };
  }

  const defaultOptIns = [
    defaultTermsAndPrivacyOptIn(params.artistName),
    defaultSmsOptIn(params.artistName),
  ];

  try {
    const { data } = await httpsCallable<
      { artistGroupId: string; artistName: string },
      OptInsResponse
    >(
      getFunctions(),
      "setfan-buildCustomOptIns",
    )({
      artistGroupId: params.artistGroupId,
      artistName: params.artistName,
    });

    params.optIns = [...defaultOptIns, ...data.optIns];

    if (data.disclaimers?.length) {
      params.disclaimers = data.disclaimers.map(styleLabelText);
    }
    if (Object.keys(data.virtualOptIns ?? {}).length) {
      params.virtualOptIns = data.virtualOptIns;
    }
  } catch (err) {
    console.error(err);
    params.optIns = [...defaultOptIns];
  }

  const version = doc(
    collection(getFirestore(), `set_fan_builder_surveys/${survey.id}/versions`),
  );

  await setDoc(survey, {
    artistGroupId: params.artistGroupId,
    createdAt: serverTimestamp(),
    draftId: version.id,
    status: "draft",
  } as SurveyBuilderRootDocument);

  await setDoc(version, removeUndefined(params));

  return { survey };
};
